import type {
  CraftRoomResponse,
  MaterialResponse,
  CraftProjectResponse,
  ProjectMaterialResponse,
  ProjectCommentResponse,
  CreateMaterialRequest,
  UpdateCraftProjectRequest,
  CreateProjectMaterialRequest,
  UpdateProjectMaterialRequest,
  ProjectTempResponse,
  UpdateTempProjectRequest,
  CraftRoomWithProjectsResponse,
  GetMaterialsResponse,
  CraftRoomMaterialsPageResponse,
  MaterialSearchResponse,
  MigrateImagesResponse,
  ContributeMaterialResponse,
  CraftRoomSettingsResponse,
  BatchUpdateResponse,
  BulkDeleteResponse,
  FolderConfigResponse,
  AssignFoldersResponse,
  UpdateFolderConfigResponse,
  ExportCSVResponse,
  ImportCSVResponse,
  MergeMaterialsResponse,
  CreateTempProjectResponse,
  CreateDraftProjectResponse,
  PersonalMaterialDetailResponse,
  CopyMaterialResponse,
  SubmitFieldSuggestionResponse,
  SendFieldSuggestionMessageResponse,
  FieldSuggestionThread,
  MaterialLinkSuggestionQueueResponse,
  ResolveMaterialLinkSuggestionResponse,
  IncidentSeverity,
  MessageResponse,
  SuccessResponse,
  PendingConfirmationsResponse,
  ResolveFieldSuggestionRequest,
  ChangeLinkRequest,
  SimpleSuccessResponse,
} from '@mrb91002/shared-types'

import { secureFetch, SECURE_API_BASE, API_BASE } from './helpers'

// Re-export shared-types used by consumers that previously imported them from this file
export type { FieldSuggestionMessage, FieldSuggestionThread } from '@mrb91002/shared-types'

// Moved to secure/patterns.ts (uploadTempPatternImage also in secure/images.ts)

// Moved to secure/patterns.ts

// Moved to secure/images.ts

// Moved to secure/profile.ts (also available via secure/images.ts unified upload)

// Moved to secure/profile.ts

// ---- PATTERN MUTATIONS (Published patterns only, NOT for drafts) ----

// Moved to secure/reviews.ts

// ---- USER ACTIONS ----

// Moved to secure/social.ts

// Moved to secure/notifications.ts

// Moved to secure/profile.ts

// Moved to secure/social.ts and secure/notifications.ts

// Moved to secure/patterns.ts

/**
 * Copy personal material to your craft room
 */
export async function copyMaterial(
  sourceMaterialId: string,
  sourceScreenName: string,
  customizations: { quantity?: number; storage_location?: string; notes?: string },
  idToken: string,
): Promise<CopyMaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-room/materials/copy`, idToken, {
    method: 'POST',
    body: JSON.stringify({
      source_material_id: sourceMaterialId,
      source_screen_name: sourceScreenName,
      customizations,
    }),
  })
}

/**
 * Contribute existing personal material to public database
 */
export async function contributeExistingMaterial(
  materialId: string,
  idToken: string,
  parentMaterialId?: string,
): Promise<ContributeMaterialResponse> {
  const body = parentMaterialId ? { parent_material_id: parentMaterialId } : {}

  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}/contribute`, idToken, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Migrate material images from temp to processed bucket
 */
export async function migrateMaterialImages(materialId: string, idToken: string): Promise<MigrateImagesResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}/migrate-images`, idToken, {
    method: 'POST',
  })
}

/**
 * Migrate project images from temp to processed bucket
 */
export async function migrateProjectImages(projectId: string, idToken: string): Promise<MigrateImagesResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/migrate-images`, idToken, {
    method: 'POST',
  })
}

// Moved to secure/patterns.ts

// Moved to secure/moderation.ts

// Moved to secure/patterns.ts

// Moved to secure/moderation.ts

// Moved to secure/patterns.ts

// Moved to secure/moderation.ts

// Moved to secure/moderation.ts

// ---- CRAFT ROOM ----

/**
 * Get own craft room with all projects (owner only - uses GSI1)
 */
export async function fetchOwnCraftRoom(idToken: string): Promise<CraftRoomWithProjectsResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-room`, idToken, {
    method: 'GET',
  })
}

/**
 * Update craft room details
 */
export async function updateCraftRoom(
  roomData: {
    title?: string
    description?: string
    custom_tabs?: string[]
    material_tab_mapping?: Record<string, string>
    room_image_url?: string
  },
  idToken: string,
): Promise<CraftRoomResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-room`, idToken, {
    method: 'PUT',
    body: JSON.stringify(roomData),
  })
}

/**
 * Get craft room materials (paginated)
 */
export async function fetchMaterials(
  page: number = 1,
  category: string | undefined,
  idToken: string,
): Promise<GetMaterialsResponse> {
  let url = `${SECURE_API_BASE}/materials?page=${page}`
  if (category && category !== 'all') {
    url += `&category=${encodeURIComponent(category)}`
  }
  return secureFetch(url, idToken, { method: 'GET' })
}

/**
 * Get user materials (all materials for inventory checking)
 */
export async function getUserMaterials(idToken: string): Promise<MaterialResponse[]> {
  // The route returns GetMaterialsResponse ({ materials, hasMore }); unwrap to the array the
  // callers (e.g. InventoryPage) expect. Returning the raw object here caused
  // "(materialsData || []).map is not a function" on the /inventory page.
  const res = await secureFetch<GetMaterialsResponse>(`${SECURE_API_BASE}/materials`, idToken, {
    method: 'GET',
  })
  return res.materials ?? []
}

/**
 * Fetch a page of the owner's craft-room materials (secure, paginated).
 *
 * Hits the secure paginated route `GET /secure/craft-room/{screenName}/materials?limit={n}&lastKey={serialized}`
 * which calls `fetchCraftRoomData` with the authenticated identity so both private and public
 * materials are included. Materials are returned newest-first. `lastKey === null` means no
 * further pages remain. Never throws for an empty page (returns `items: []`).
 */
export async function fetchSecureCraftRoomMaterials(
  screenName: string,
  idToken: string,
  opts: { limit: number; lastKey?: string | null },
): Promise<CraftRoomMaterialsPageResponse> {
  let url = `${SECURE_API_BASE}/craft-room/${encodeURIComponent(screenName)}/materials?limit=${opts.limit}`
  if (typeof opts.lastKey === 'string' && opts.lastKey.length > 0) {
    url += `&lastKey=${encodeURIComponent(opts.lastKey)}`
  }
  const res = await secureFetch<CraftRoomMaterialsPageResponse>(url, idToken, { method: 'GET' })
  return { items: res.items ?? [], lastKey: res.lastKey ?? null }
}

// getUserSettings moved to secure/profile.ts

/**
 * Add material to inventory
 */
export async function addMaterial(materialData: CreateMaterialRequest, idToken: string): Promise<MaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials`, idToken, {
    method: 'POST',
    body: JSON.stringify(materialData),
  })
}

/**
 * Get a single personal material (full record with all fields)
 */
export async function getPersonalMaterial(
  materialId: string,
  idToken: string,
): Promise<PersonalMaterialDetailResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}`, idToken)
}

/**
 * Update material (private - user's craft room)
 */
export async function updateMaterial(
  materialId: string,
  materialData: Partial<CreateMaterialRequest>,
  idToken: string,
): Promise<MaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(materialData),
  })
}

/**
 * Update public material (moderator only)
 */
export async function updatePublicMaterial(
  materialId: string,
  materialData: Partial<CreateMaterialRequest>,
  idToken: string,
): Promise<MaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/public/${encodeURIComponent(materialId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(materialData),
  })
}

/**
 * Delete material
 */
export async function deleteMaterial(materialId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/materials/${encodeURIComponent(materialId)}`, idToken, {
    method: 'DELETE',
  })
}

// ---- TEMP PROJECT ROUTES ----

/**
 * Create temp project
 */
export async function createTempProject(idToken: string): Promise<CreateTempProjectResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/temp`, idToken, {
    method: 'POST',
  })
}

/**
 * Get temp project
 */
export async function fetchTempProject(projectId: string, idToken: string): Promise<ProjectTempResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/temp/${encodeURIComponent(projectId)}`, idToken, {
    method: 'GET',
  })
}

/**
 * Update temp project
 */
export async function updateTempProject(
  projectId: string,
  data: UpdateTempProjectRequest,
  idToken: string,
): Promise<ProjectTempResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/temp/${encodeURIComponent(projectId)}`, idToken, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

/**
 * Delete temp project
 */
export async function deleteTempProject(projectId: string, idToken: string): Promise<MessageResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/temp/${encodeURIComponent(projectId)}`, idToken, {
    method: 'DELETE',
  })
}

/**
 * Convert temp project to draft
 */
export async function createDraftProject(
  data: { tempProjectId: string },
  idToken: string,
): Promise<CreateDraftProjectResponse> {
  return secureFetch(`${SECURE_API_BASE}/project/draft`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Update draft project
 */
export async function updateDraftProject(
  projectId: string,
  data: UpdateCraftProjectRequest,
  idToken: string,
): Promise<SuccessResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/draft`, idToken, {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

// ---- LEGACY PROJECT ROUTES ----

/**
 * Get draft project with assembled materials
 */
export async function getDraftProject(projectId: string, idToken: string): Promise<CraftProjectResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/draft`, idToken, {
    method: 'GET',
  })
}

/**
 * Publish project (switches GSI4 → GSI3, increments counts)
 */
export async function publishCraftProject(projectId: string, idToken: string): Promise<MessageResponse> {
  return secureFetch<MessageResponse>(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/publish`, idToken, {
    method: 'POST',
  })
}

/**
 * Unpublish project (switches GSI3 → GSI4, decrements counts)
 */
export async function unpublishCraftProject(projectId: string, idToken: string): Promise<MessageResponse> {
  return secureFetch<MessageResponse>(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/unpublish`, idToken, {
    method: 'POST',
  })
}

/**
 * Update craft project draft
 */
export async function updateCraftProject(
  projectId: string,
  projectData: UpdateCraftProjectRequest,
  idToken: string,
): Promise<SuccessResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/draft`, idToken, {
    method: 'PUT',
    body: JSON.stringify(projectData),
  })
}

/**
 * Get craft project
 */
export async function fetchCraftProject(projectId: string, idToken?: string): Promise<CraftProjectResponse> {
  if (idToken) {
    return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`, idToken, {
      method: 'GET',
    })
  } else {
    // Public access for non-authenticated users
    const response = await fetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`)
    if (!response.ok) {
      throw new Error('Project not found')
    }
    return response.json() as Promise<CraftProjectResponse>
  }
}



/**
 * Archive craft project (removes from GSI2 active projects)
 */
export async function archiveCraftProject(projectId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/archive`, idToken, {
    method: 'POST',
  })
}

/**
 * Unarchive craft project (adds back to GSI2 active projects)
 */
export async function unarchiveCraftProject(projectId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/unarchive`, idToken, {
    method: 'POST',
  })
}

/**
 * Get craft room privacy settings
 */
export async function fetchCraftRoomSettings(idToken: string): Promise<CraftRoomSettingsResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-room/settings`, idToken, { method: 'GET' })
}

/**
 * Update craft room privacy settings
 */
export async function updateCraftRoomSettings(
  settings: {
    is_public?: boolean
    allow_material_discovery?: boolean
    show_purchase_info?: boolean
    show_material_checker?: boolean
  },
  idToken: string,
): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/craft-room/settings`, idToken, {
    method: 'PUT',
    body: JSON.stringify(settings),
  })
}

// Upload functions available via secure/images.ts unified upload:
// - uploadCraftRoomImage
// - uploadMaterialImage
// - uploadTempCraftProjectImage
// - uploadCraftProjectImage
// - uploadTempMaterialImage

/**
 * Get craft project materials
 */
export async function fetchProjectMaterials(projectId: string, idToken: string): Promise<ProjectMaterialResponse[]> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials`, idToken, {
    method: 'GET',
  })
}

/**
 * Add material to craft project
 */
export async function addProjectMaterial(
  projectId: string,
  materialData: CreateProjectMaterialRequest,
  idToken: string,
): Promise<ProjectMaterialResponse> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials`, idToken, {
    method: 'POST',
    body: JSON.stringify(materialData),
  })
}

/**
 * Update craft project material
 */
export async function updateProjectMaterial(
  projectId: string,
  materialId: string,
  materialData: UpdateProjectMaterialRequest,
  idToken: string,
): Promise<ProjectMaterialResponse> {
  return secureFetch(
    `${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials/${encodeURIComponent(materialId)}`,
    idToken,
    {
      method: 'PUT',
      body: JSON.stringify(materialData),
    },
  )
}

/**
 * Delete craft project material
 */
export async function deleteProjectMaterial(projectId: string, materialId: string, idToken: string): Promise<void> {
  return secureFetch(
    `${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/materials/${encodeURIComponent(materialId)}`,
    idToken,
    {
      method: 'DELETE',
    },
  )
}

/**
 * Get craft project comments
 */
export async function fetchProjectComments(projectId: string): Promise<ProjectCommentResponse[]> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/comments`, '', {
    method: 'GET',
  })
}

/**
 * Add comment to craft project (requires authentication)
 */
export async function addProjectComment(
  projectId: string,
  content: string,
  idToken: string,
): Promise<ProjectCommentResponse> {
  return secureFetch(`${SECURE_API_BASE}/craft-projects/${encodeURIComponent(projectId)}/comments`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content }),
  })
}

/**
 * Delete project comment (owner only)
 */
export async function deleteProjectComment(projectId: string, commentId: string, idToken: string): Promise<void> {
  return secureFetch(
    `${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}/comments/${encodeURIComponent(commentId)}`,
    idToken,
    {
      method: 'DELETE',
    },
  )
}

/**
 * Get single material details (public API - no auth required)
 */
export async function fetchMaterial(materialId: string): Promise<MaterialResponse> {
  const resp = await fetch(`${API_BASE}/materials/${encodeURIComponent(materialId)}`)
  if (!resp.ok) throw new Error('Material not found')
  return resp.json() as Promise<MaterialResponse>
}

/**
 * Search materials (public + private combined).
 *
 * `includePublic` (default `true` for back-compat) mirrors `includePersonal`: when set to `false`
 * the route runs ONLY the caller's personal query (inventory personal-only mode, Req 4.1/4.2) and
 * the `includePublic=false` query param is set. Existing callers that omit it keep the previous
 * public+personal behavior.
 *
 * `lastKey` forwards the opaque OpenSearch pagination cursor returned by a prior page so callers can
 * page additional results via `loadMore`. Omitted/`null` requests page 1.
 */
export async function searchMaterials(
  query: string,
  idToken: string,
  limit: number = 20,
  includePersonal: boolean = true,
  includePublic: boolean = true,
  lastKey?: string | null,
): Promise<MaterialSearchResponse> {
  const params = new URLSearchParams({ q: query, limit: limit.toString() })
  if (!includePersonal) params.set('includePersonal', 'false')
  if (!includePublic) params.set('includePublic', 'false')
  if (typeof lastKey === 'string' && lastKey.length > 0) params.set('lastKey', lastKey)
  return secureFetch(`${SECURE_API_BASE}/materials/search?${params}`, idToken, {
    method: 'GET',
  })
}
// Moved to secure/moderation.ts

// Moved to secure/profile.ts

// ---- MATERIAL BATCH OPERATIONS ----

/**
 * Max number of material changes per batch-update request.
 *
 * DynamoDB's TransactWrite / BatchWrite ceiling is 25 items, so the backend
 * batch-update route can only safely apply 25 changes per call. We enforce that
 * ceiling here on the client (design decision #3, Requirement 5.2/5.3,
 * Correctness Property 7 — chunk safety) so every bulk caller inherits chunking
 * without duplicating the logic at each call site.
 */
export const BATCH_UPDATE_CHUNK_SIZE = 25

/**
 * Merge the per-chunk `BatchUpdateResponse`s from a chunked batch-update into a
 * single aggregate response with the same contract callers expect:
 * - `updated` counts are summed across all chunks.
 * - `message` strings are joined (deduped) so nothing is silently dropped.
 */
function mergeBatchUpdateResponses(responses: BatchUpdateResponse[]): BatchUpdateResponse {
  const updated = responses.reduce((sum, r) => sum + (r?.updated ?? 0), 0)
  const messages = responses
    .map((r) => r?.message)
    .filter((m): m is string => typeof m === 'string' && m.length > 0)
  const uniqueMessages = Array.from(new Set(messages))
  return {
    message: uniqueMessages.length > 0 ? uniqueMessages.join('; ') : `Updated ${updated} materials`,
    updated,
  }
}

/**
 * Batch update multiple materials.
 *
 * Changes are chunked by `material_id` into slices of at most
 * {@link BATCH_UPDATE_CHUNK_SIZE} (25) and issued as ⌈N/25⌉ sequential
 * `POST /materials/batch-update` requests, keeping each request under the
 * DynamoDB TransactWrite/BatchWrite ceiling (design decision #3, Req 5.2/5.3,
 * Correctness Property 7). The per-chunk responses are merged into a single
 * aggregate `BatchUpdateResponse` so callers see the same contract regardless of
 * how many chunks were sent. A call with ≤25 changes still issues exactly one
 * request; a call with zero changes issues no request.
 */
export async function batchUpdateMaterials(
  changes: Record<string, Partial<CreateMaterialRequest>>,
  idToken: string,
): Promise<BatchUpdateResponse> {
  const ids = Object.keys(changes)

  // Zero changes: don't POST an empty batch — return a zeroed response.
  if (ids.length === 0) {
    return { message: 'Updated 0 materials', updated: 0 }
  }

  const responses: BatchUpdateResponse[] = []
  for (let i = 0; i < ids.length; i += BATCH_UPDATE_CHUNK_SIZE) {
    const sliceIds = ids.slice(i, i + BATCH_UPDATE_CHUNK_SIZE)
    const chunk: Record<string, Partial<CreateMaterialRequest>> = {}
    for (const id of sliceIds) {
      chunk[id] = changes[id]!
    }
    const res = await secureFetch<BatchUpdateResponse>(`${SECURE_API_BASE}/materials/batch-update`, idToken, {
      method: 'POST',
      body: JSON.stringify({ changes: chunk }),
    })
    responses.push(res)
  }

  return mergeBatchUpdateResponses(responses)
}

/**
 * Bulk delete multiple materials
 */
export async function bulkDeleteMaterials(materialIds: string[], idToken: string): Promise<BulkDeleteResponse> {
  return secureFetch(`${SECURE_API_BASE}/materials/bulk-delete`, idToken, {
    method: 'POST',
    body: JSON.stringify({ materialIds }),
  })
}

/**
 * Delete craft project
 */
export async function deleteCraftProject(projectId: string, idToken: string): Promise<void> {
  return secureFetch(`${SECURE_API_BASE}/projects/${encodeURIComponent(projectId)}`, idToken, {
    method: 'DELETE',
  })
}

// ---- FOLDER MANAGEMENT ----

/**
 * Get user's folder configuration
 */
export async function getFolderConfig(idToken: string): Promise<FolderConfigResponse> {
  return secureFetch(`${SECURE_API_BASE}/folders/config`, idToken, {
    method: 'GET',
  })
}

/**
 * Update user's folder configuration
 */
export async function updateFolderConfig(
  folders: Array<{ name: string; icon?: string; color?: string }>,
  idToken: string,
): Promise<UpdateFolderConfigResponse> {
  return secureFetch(`${SECURE_API_BASE}/folders/config`, idToken, {
    method: 'PUT',
    body: JSON.stringify({ folders }),
  })
}

/**
 * Assign materials to folders
 */
export async function assignMaterialsToFolders(
  materialIds: string[],
  folderNames: string[],
  idToken: string,
): Promise<AssignFoldersResponse> {
  // Backend expects { assignments: Record<string, string[]> } where key is materialId and value is folders array
  const assignments: Record<string, string[]> = {}
  for (const materialId of materialIds) {
    assignments[materialId] = folderNames
  }
  return secureFetch(`${SECURE_API_BASE}/folders/assign`, idToken, {
    method: 'POST',
    body: JSON.stringify({ assignments }),
  })
}

// ---- INVITE REQUESTS ----
// Moved to secure/access.ts

// Moved to secure/feedback.ts

export async function fetchMaterialLinkSuggestions(idToken: string): Promise<MaterialLinkSuggestionQueueResponse> {
  return secureFetch<MaterialLinkSuggestionQueueResponse>(
    `${SECURE_API_BASE}/material-link-suggestions/moderation`,
    idToken,
    { method: 'GET' },
  )
}

export async function resolveMaterialLinkSuggestion(
  suggestionId: string,
  resolution: 'approved' | 'rejected',
  notes: string | undefined,
  idToken: string,
  options?: {
    is_violation?: boolean
    severity?: IncidentSeverity
  },
): Promise<ResolveMaterialLinkSuggestionResponse> {
  // BE-1: backend handler reads `{ decision, notes }`, NOT
  // `{ action, moderator_notes }`. The previous fetcher's body shape would
  // have produced a 400 — only invisible because the dispatch never reached
  // this fetcher.
  // BF-026: the backend registers this route WITHOUT the `moderation/` prefix
  // (`material-link-suggestions/{id}/moderate`), matching every sibling
  // link-suggestion route (create/queue/pending/confirm) and the canonical
  // contract in `moderation-system.md`. The `/moderation/`-prefixed path this
  // fetcher used never resolved → every moderate POST 404'd.
  return secureFetch<ResolveMaterialLinkSuggestionResponse>(
    `${SECURE_API_BASE}/material-link-suggestions/${encodeURIComponent(suggestionId)}/moderate`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({
        decision: resolution,
        notes,
        // Decision D: forward optional is_violation + severity from the
        // dashboard's violation-checkbox UX.
        ...(options?.is_violation !== undefined && { is_violation: options.is_violation }),
        ...(options?.severity && { severity: options.severity }),
      }),
    },
  )
}

// Moved to secure/patterns.ts

// Moved to secure/patterns.ts

/**
 * Merge duplicate materials (moderator only)
 */
export async function mergeDuplicateMaterials(
  oldMaterialId: string,
  newMaterialId: string,
  reason: string,
  idToken: string,
): Promise<MergeMaterialsResponse> {
  return secureFetch(`${SECURE_API_BASE}/moderation/materials/merge-duplicates`, idToken, {
    method: 'POST',
    body: JSON.stringify({
      old_material_id: oldMaterialId,
      new_material_id: newMaterialId,
      reason,
    }),
  })
}

/**
 * Merge materials by source/target (moderator only).
 */
export async function mergeMaterials(
  sourceMaterialId: string,
  targetMaterialId: string,
  idToken: string,
): Promise<MergeMaterialsResponse> {
  return secureFetch<MergeMaterialsResponse>(
    `${SECURE_API_BASE}/materials/merge`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({
        source_material_id: sourceMaterialId,
        target_material_id: targetMaterialId,
      }),
    },
  )
}

// Moved to secure/comments.ts

// Moved to secure/moderation.ts

// Moved to secure/patterns.ts

// Moved to secure/moderation.ts

// Moved to secure/patterns.ts

// Moved to secure/social.ts

// Moved to secure/collections.ts

// ---- INVENTORY CSV EXPORT/IMPORT ----

// package-local: ExportOptions is frontend-only (CSV export form state)
export interface ExportOptions {
  exportType: 'all' | 'selected'
  columns: string[]
  selectedIds?: string[]
}

// package-local: ImportData is frontend-only (CSV import form state)
export interface ImportData {
  materials: Record<string, string>[]
  columnMapping: Record<string, string>
}

/**
 * Export inventory to CSV
 */
export async function exportInventoryCSV(options: ExportOptions, idToken: string): Promise<ExportCSVResponse> {
  const body = {
    columns: options.columns.map((id) => ({ id, label: id })),
    selectedIds: options.exportType === 'selected' ? options.selectedIds : undefined,
  }

  return secureFetch(`${SECURE_API_BASE}/inventory/export`, idToken, {
    method: 'POST',
    body: JSON.stringify(body),
  })
}

/**
 * Import inventory from CSV
 */
export async function importInventoryCSV(data: ImportData, idToken: string): Promise<ImportCSVResponse> {
  return secureFetch(`${SECURE_API_BASE}/inventory/import`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

/**
 * Download CSV file (client-side utility)
 */
export function downloadCSV(csvContent: string, filename: string): void {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

/**
 * Parse CSV file (client-side utility)
 */
export function parseCSVFile(file: File): Promise<{ headers: string[]; data: Record<string, string>[] }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const text = e.target?.result as string
        const lines = text.split('\n').filter((line) => line.trim())

        if (lines.length === 0) {
          reject(new Error('Empty CSV file'))
          return
        }

        const headers = lines[0]!.split(',').map((h) => h.trim().replace(/"/g, ''))
        const data = lines.slice(1).map((line) => {
          const values = line.split(',').map((v) => v.trim().replace(/"/g, ''))
          const row: Record<string, string> = {}
          headers.forEach((header, index) => {
            row[header] = values[index] || ''
          })
          return row
        })

        resolve({ headers, data })
      } catch (error: unknown) {
        reject(error)
      }
    }

    reader.onerror = () => reject(new Error('Failed to read file'))
    reader.readAsText(file)
  })
}

export async function submitFieldSuggestion(
  data: {
    request_type: 'add' | 'remove' | 'modify'
    category: string
    subcategory?: string
    field_name: string
    modify_suggestion?: string
    additional_info?: string
  },
  idToken: string,
): Promise<SubmitFieldSuggestionResponse> {
  return secureFetch(`${SECURE_API_BASE}/field-suggestions`, idToken, {
    method: 'POST',
    body: JSON.stringify(data),
  })
}

export async function dismissFieldSuggestion(
  suggestionId: string,
  idToken: string,
  options?: {
    is_violation?: boolean
    severity?: IncidentSeverity
    notes?: string
  },
): Promise<void> {
  // Decision D — additive body. The dismiss handler also accepts the body as
  // a JSON document (the backend tries-parses it). Legacy callers omit the
  // body entirely; the new behavior reads `is_violation`, `severity`, `notes`.
  const hasBody = options !== undefined && (
    options.is_violation !== undefined || options.severity !== undefined || options.notes !== undefined
  )
  await secureFetch(`${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(suggestionId)}`, idToken, {
    method: 'DELETE',
    ...(hasBody
      ? {
          body: JSON.stringify({
            ...(options?.is_violation !== undefined && { is_violation: options.is_violation }),
            ...(options?.severity && { severity: options.severity }),
            ...(options?.notes && { notes: options.notes }),
          }),
        }
      : {}),
  })
}

/**
 * Resolve a field suggestion as implemented or planned (Req 11.1, 11.2).
 * POST /field-suggestions/{id}/resolve
 */
export async function resolveFieldSuggestion(
  id: string,
  body: ResolveFieldSuggestionRequest,
  idToken: string,
): Promise<SimpleSuccessResponse> {
  return secureFetch<SimpleSuccessResponse>(
    `${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(id)}/resolve`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

/**
 * Change which public material a personal material is linked to (Req 21.3 / J7).
 * POST /craft-room/materials/{materialId}/change-link
 *
 * Re-runs the link logic with the new public material ID, updating all pattern
 * drafts, project drafts, and material usage records. There is no unlink option.
 */
export async function changeMaterialLink(
  materialId: string,
  body: ChangeLinkRequest,
  idToken: string,
): Promise<SimpleSuccessResponse> {
  return secureFetch<SimpleSuccessResponse>(
    `${SECURE_API_BASE}/craft-room/materials/${encodeURIComponent(materialId)}/change-link`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  )
}

export async function getFieldSuggestionMessages(suggestionId: string, idToken: string): Promise<FieldSuggestionThread> {
  return secureFetch(`${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(suggestionId)}/messages`, idToken, {
    method: 'GET',
  })
}

export async function sendModeratorMessage(suggestionId: string, content: string, idToken: string): Promise<SendFieldSuggestionMessageResponse> {
  return secureFetch(`${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(suggestionId)}/message`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content }),
  })
}

export async function sendUserReply(suggestionId: string, content: string, idToken: string): Promise<SendFieldSuggestionMessageResponse> {
  return secureFetch(`${SECURE_API_BASE}/field-suggestions/${encodeURIComponent(suggestionId)}/reply`, idToken, {
    method: 'POST',
    body: JSON.stringify({ content }),
  })
}

/**
 * Fetch pending link confirmations for the current user (owner).
 */
export async function fetchPendingLinkConfirmations<T = PendingConfirmationsResponse>(idToken: string): Promise<T> {
  return secureFetch<T>(
    `${SECURE_API_BASE}/material-link-suggestions/pending`,
    idToken,
    { method: 'GET' },
  )
}

/**
 * Accept or reject a pending link confirmation.
 */
export async function confirmLinkSuggestion(
  suggestionId: string,
  action: 'accept' | 'reject',
  idToken: string,
): Promise<SuccessResponse> {
  // BF-041: the backend `confirmLinkSuggestion` handler reads `{ decision }` and REQUIRES it to be
  // one of `'confirmed' | 'rejected'` (`['confirmed','rejected'].includes(decision)` — anything else
  // 400s "Invalid request"). This fetcher previously sent `{ action: 'accept' | 'reject' }`, so
  // EVERY owner confirm/reject 400'd and the owner-confirmation leg of the community link-suggestion
  // lifecycle was dead end-to-end. Map the UI's accept/reject onto the backend's confirmed/rejected
  // contract (matching every sibling route which is verb-consistent with the handler).
  const decision = action === 'accept' ? 'confirmed' : 'rejected'
  return secureFetch<SuccessResponse>(
    `${SECURE_API_BASE}/material-link-suggestions/${encodeURIComponent(suggestionId)}/confirm`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({ decision }),
    },
  )
}

/**
 * Submit a create-parent material contribution proposal.
 */
export async function submitCreateParentContribution(
  data: {
    suggested_parent_name: string
    description: string
    variation_ids: string[]
    brand: string
    category: string
  },
  idToken: string,
): Promise<ContributeMaterialResponse> {
  return secureFetch<ContributeMaterialResponse>(
    `${SECURE_API_BASE}/materials/contribute`,
    idToken,
    {
      method: 'POST',
      body: JSON.stringify({
        submission_type: 'create_parent',
        parent_request: data,
        material_data: {},
      }),
    },
  )
}
